import React, { useState, useEffect } from 'react'
import { X, Grid3X3, RefreshCw, Download, Eye } from 'lucide-react'
import { templateAPI } from '../../services/api'

const SampleGenerationDialog = ({ 
  isOpen, 
  onClose, 
  templateId, 
  templateContent,
  onGenerate9Samples 
}) => {
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedSamples, setGeneratedSamples] = useState([])
  const [generationStatus, setGenerationStatus] = useState('idle') // idle, generating, completed, error
  const [error, setError] = useState(null)

  // Load previously generated samples when dialog opens
  useEffect(() => {
    if (isOpen && templateId) {
      loadPreviousSamples()
    }
  }, [isOpen, templateId])

  const loadPreviousSamples = async () => {
    try {
      // This would be an API call to get previously generated samples
      // For now, we'll simulate with empty array
      setGeneratedSamples([])
    } catch (error) {
      console.error('Error loading previous samples:', error)
    }
  }

  const handleGenerateSamples = async () => {
    if (!templateId) return

    setIsGenerating(true)
    setGenerationStatus('generating')
    setError(null)

    try {
      // Call the API to generate 9 sample designs
      const response = await templateAPI.generate9Samples(templateId, {
        template_content: templateContent,
        count: 9
      })

      if (response.data.success) {
        setGeneratedSamples(response.data.data.samples || [])
        setGenerationStatus('completed')
      } else {
        setError(response.data.message || 'Failed to generate samples')
        setGenerationStatus('error')
      }
    } catch (error) {
      console.error('Error generating samples:', error)
      setError('Failed to generate samples. Please try again.')
      setGenerationStatus('error')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleRetryGeneration = () => {
    handleGenerateSamples()
  }

  const handleViewSample = (sample) => {
    // Open sample in new tab or modal
    if (sample.preview_url) {
      window.open(sample.preview_url, '_blank')
    }
  }

  const handleDownloadSample = (sample) => {
    // Download sample
    if (sample.download_url) {
      const link = document.createElement('a')
      link.href = sample.download_url
      link.download = sample.filename || `sample_${sample.id}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Grid3X3 className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Sample Design Generation
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Status Section */}
          <div className="mb-6">
            {generationStatus === 'idle' && (
              <div className="text-center py-8">
                <Grid3X3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Generate 9 Different Sample Designs
                </h3>
                <p className="text-gray-600 mb-6">
                  Create a 3x3 grid of sample designs with randomized field values
                </p>
                <button
                  onClick={handleGenerateSamples}
                  disabled={isGenerating}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 mx-auto"
                >
                  <Grid3X3 className="w-5 h-5" />
                  <span>Generate Samples</span>
                </button>
              </div>
            )}

            {generationStatus === 'generating' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Generating Sample Designs...
                </h3>
                <p className="text-gray-600">
                  This may take a few moments. Please wait while we create your samples.
                </p>
              </div>
            )}

            {generationStatus === 'error' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Generation Failed
                </h3>
                <p className="text-gray-600 mb-6">
                  {error || 'An error occurred while generating samples.'}
                </p>
                <button
                  onClick={handleRetryGeneration}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 mx-auto"
                >
                  <RefreshCw className="w-5 h-5" />
                  <span>Retry Generation</span>
                </button>
              </div>
            )}

            {generationStatus === 'completed' && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Generated Sample Designs ({generatedSamples.length}/9)
                  </h3>
                  <button
                    onClick={handleRetryGeneration}
                    className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center space-x-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Re-generate</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sample Grid */}
          {generatedSamples.length > 0 && (
            <div className="grid grid-cols-3 gap-4">
              {generatedSamples.map((sample, index) => (
                <div key={sample.id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="aspect-square bg-white rounded-lg border border-gray-200 mb-3 flex items-center justify-center overflow-hidden">
                    {sample.preview_url ? (
                      <img
                        src={sample.preview_url}
                        alt={`Sample ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-gray-400 text-center">
                        <Grid3X3 className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">Sample {index + 1}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Sample {index + 1}
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewSample(sample)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="View Sample"
                      >
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDownloadSample(sample)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="Download Sample"
                      >
                        <Download className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default SampleGenerationDialog
